import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Grid, Card, CardContent, Button, Tab, Tabs,
  TextField, IconButton, Divider, FormControl, InputLabel, MenuItem,
  Select, Chip, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TablePagination, Tooltip, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, Snackbar, Alert,
  FormHelperText,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Check as CheckIcon,
  Help as HelpIcon,
  SentimentSatisfied as SmileyIcon,
  SentimentDissatisfied as SadIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon,
  ViewList as ViewListIcon,
  ViewModule as ViewModuleIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import apiClient from '../services/apiClient';
import { useLanguage } from "../context/LanguageContext";
import TagFilter from '../components/TagFilter';
import WordDialog from '../components/WordDialog';

// Language options
const languageOptions = [
  { code: 'en', name: 'English' },
  { code: 'fr', name: 'French' },
  { code: 'es', name: 'Spanish' },
  { code: 'de', name: 'German' },
  { code: 'it', name: 'Italian' },
  { code: 'ro', name: 'Romanian' },
  { code: 'pt', name: 'Portuguese' },
  { code: 'ja', name: 'Japanese' }
];

// Knowledge level options
const knowledgeLevels = [
  { value: 1, label: "Don't Know", color: 'error', icon: <SadIcon /> },
  { value: 2, label: 'Learning', color: 'warning', icon: <HelpIcon /> },
  { value: 3, label: 'Know Well', color: 'success', icon: <SmileyIcon /> }
];

const VocabularyPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  // State for vocabulary items
  const [selectedFilterTags, setSelectedFilterTags] = useState([]);
  const [tagFilterMode, setTagFilterMode] = useState('OR');
  const [vocabulary, setVocabulary] = useState([]);
  const [filteredVocabulary, setFilteredVocabulary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({});

  // State for UI
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLevel, setSelectedLevel] = useState(0); // 0 means all levels
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [sortField, setSortField] = useState('word');
  const [sortDirection, setSortDirection] = useState('asc');

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const { selectedLanguage, setSelectedLanguage } = useLanguage();

  // New word dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState('add'); // 'add' or 'edit'
  const [editingItemId, setEditingItemId] = useState(null);
  const [newWord, setNewWord] = useState({
    word: '',
    translation: '',
    context: '',
    knowledge_level: 1,
    language_code: '',
    tags: []
  });
  const [dialogErrors, setDialogErrors] = useState({});

  // Notification state
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Effect to load vocabulary data - Updated with tag filters
  useEffect(() => {
    if (user) {
      fetchVocabulary();
      fetchStats();
    }
  }, [selectedFilterTags, tagFilterMode, selectedLanguage, selectedLevel]);

  // Effect to filter and sort vocabulary based on selected options
  useEffect(() => {
    if (vocabulary.length === 0) {
      setFilteredVocabulary([]);
      return;
    }

    let filtered = [...vocabulary];

    // Note: Language filtering is already done on the server side
    // Only filter locally by search term since that's more responsive

    // Filter by search term
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase().trim();
      filtered = filtered.filter(
        item =>
          item.word.toLowerCase().includes(term) ||
          item.translation.toLowerCase().includes(term)
      );
    }

    // Sort
    filtered.sort((a, b) => {
      let comparison = 0;

      if (sortField === 'word') {
        comparison = a.word.localeCompare(b.word);
      } else if (sortField === 'translation') {
        comparison = a.translation.localeCompare(b.translation);
      } else if (sortField === 'knowledge_level') {
        comparison = a.knowledge_level - b.knowledge_level;
      } else if (sortField === 'last_reviewed') {
        comparison = new Date(a.last_reviewed || 0) - new Date(b.last_reviewed || 0);
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });

    setFilteredVocabulary(filtered);
    setPage(0); // Reset to first page when filters change
  }, [vocabulary, searchTerm, sortField, sortDirection]);

  // Fetch vocabulary data - Updated to include tags
  const fetchVocabulary = async () => {
    setLoading(true);
    try {
      const filters = {};

      // Only add filters if they have values
      if (selectedLanguage) {
        filters.language_code = selectedLanguage;
      }
      if (selectedLevel !== 0) {
        filters.knowledge_level = selectedLevel;
      }
      if (selectedFilterTags && selectedFilterTags.length > 0) {
        filters.tags = selectedFilterTags.map(t => t.tag_name);
        filters.tag_mode = tagFilterMode;
      }

      const response = await apiClient.vocabulary.getVocabularyItems(filters);
      setVocabulary(response.data.vocabulary || []);
    } catch (error) {
      console.error('Error fetching vocabulary:', error);
      showNotification('Failed to load vocabulary', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Fetch vocabulary statistics
  const fetchStats = async () => {
    try {
      const response = await apiClient.vocabulary.getVocabularyStats();
      setStats(response.data.vocabulary_stats || {});
    } catch (error) {
      console.error('Error fetching vocabulary stats:', error);
    }
  };

  // Open dialog to add a new word
  const handleAddWord = () => {
    setDialogMode('add');
    setNewWord({
      word: '',
      translation: '',
      context: '',
      knowledge_level: 1,
      language_code: selectedLanguage || (user?.languages?.[0]?.language_code || ''),
      tags: []
    });
    setDialogErrors({});
    setDialogOpen(true);
  };

  // Open dialog to edit a word
  const handleEditWord = (item) => {
    setDialogMode('edit');
    setEditingItemId(item.id);
    setNewWord({
      word: item.word,
      translation: item.translation,
      context: item.context || '',
      knowledge_level: item.knowledge_level,
      language_code: item.language_code,
      tags: item.tags || []
    });
    setDialogErrors({});
    setDialogOpen(true);
  };

  // Handle dialog close
  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  // Updated save handler for WordDialog
  const handleSaveWord = async (wordData) => {
    try {
      if (dialogMode === 'edit' && editingItemId) {
        // Edit existing word
        const response = await apiClient.vocabulary.updateVocabularyItem(
          editingItemId,
          {
            translation: wordData.translation,
            context: wordData.context,
            knowledge_level: wordData.knowledge_level,
            tags: wordData.tags
          }
        );

        // Update local state
        setVocabulary(prev =>
          prev.map(item =>
            item.id === editingItemId ? response.data.vocabulary_item : item
          )
        );
        showNotification('Word updated successfully', 'success');
      } else {
        // Add new word
        const response = await apiClient.vocabulary.addVocabularyItem(wordData);

        // Update local state
        setVocabulary(prev => [...prev, response.data.vocabulary_item]);
        showNotification('Word added successfully', 'success');
      }

      // Update stats
      fetchStats();

      // Close dialog
      setDialogOpen(false);
    } catch (error) {
      console.error('Error saving vocabulary item:', error);
      if (error.response?.data?.error?.includes('already in your vocabulary')) {
        showNotification('This word already exists in your vocabulary', 'error');
      } else {
        showNotification('Failed to save word', 'error');
      }
    }
  };

  // Delete a word
  const handleDeleteWord = async (id) => {
    if (!window.confirm('Are you sure you want to delete this vocabulary item?')) {
      return;
    }

    try {
      await apiClient.vocabulary.deleteVocabularyItem(id);

      // Update local state
      setVocabulary(prev => prev.filter(item => item.id !== id));
      showNotification('Word deleted successfully', 'success');

      // Update stats
      fetchStats();
    } catch (error) {
      console.error('Error deleting vocabulary item:', error);
      showNotification('Failed to delete word', 'error');
    }
  };

  // Update knowledge level
  const handleUpdateKnowledgeLevel = async (id, level) => {
    try {
      await apiClient.vocabulary.updateVocabularyItem(id, { knowledge_level: level });

      // Update local state
      setVocabulary(prev =>
        prev.map(item =>
          item.id === id ? { ...item, knowledge_level: level, last_reviewed: new Date().toISOString() } : item
        )
      );

      // Update stats
      fetchStats();
    } catch (error) {
      console.error('Error updating knowledge level:', error);
      showNotification('Failed to update knowledge level', 'error');
    }
  };

  // Show notification
  const showNotification = (message, severity = 'success') => {
    setNotification({
      open: true,
      message,
      severity
    });
  };

  // Close notification
  const handleCloseNotification = () => {
    setNotification(prev => ({
      ...prev,
      open: false
    }));
  };

  // Handle pagination change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handle language filter change
  const handleLanguageChange = (e) => {
    setSelectedLanguage(e.target.value);
  };

  // Handle knowledge level filter change
  const handleLevelChange = (e) => {
    setSelectedLevel(Number(e.target.value));
  };

  // Handle search term change
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  // Handle sort change
  const handleSortChange = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Handle view mode change
  const handleViewModeChange = (mode) => {
    setViewMode(mode);
  };

  // Format last reviewed date
  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  // Get language display name
  const getLanguageDisplayName = (code) => {
    const language = languageOptions.find(lang => lang.code === code);
    return language ? language.name : code;
  };

  // Render content
  return (
    <Box sx={{ pb: '60px', pl: 0, pr: 0 }}>
      <Typography variant="h4" gutterBottom>
        My Vocabulary
      </Typography>

      {/* Filters and controls */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4} md={3} sx={{ display: "none" }}>
              <FormControl fullWidth size="small">
                <InputLabel id="language-filter-label">Language</InputLabel>
                <Select
                  labelId="language-filter-label"
                  id="language-filter"
                  value={selectedLanguage}
                  onChange={handleLanguageChange}
                  label="Language"
                >
                  <MenuItem value="">All Languages</MenuItem>
                  {languageOptions.map(lang => (
                    <MenuItem value={lang.code} key={lang.code}>
                      {lang.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={4} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel id="level-filter-label">Knowledge Level</InputLabel>
                <Select
                  labelId="level-filter-label"
                  id="level-filter"
                  value={selectedLevel}
                  onChange={handleLevelChange}
                  label="Knowledge Level"
                >
                  <MenuItem value={0}>All Levels</MenuItem>
                  {knowledgeLevels.map(level => (
                    <MenuItem value={level.value} key={level.value}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {React.cloneElement(level.icon, { fontSize: 'small', color: level.color, sx: { mr: 1 } })}
                        {level.label}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={4} md={4}>
              <TextField
                fullWidth
                size="small"
                label="Search"
                value={searchTerm}
                onChange={handleSearchChange}
                InputProps={{
                  endAdornment: <SearchIcon color="action" />
                }}
              />
            </Grid>

            <Grid item xs={6} sm={6} md={1}>
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <Tooltip title="Grid View">
                  <IconButton
                    color={viewMode === 'grid' ? 'primary' : 'default'}
                    onClick={() => handleViewModeChange('grid')}
                  >
                    <ViewModuleIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="List View">
                  <IconButton
                    color={viewMode === 'list' ? 'primary' : 'default'}
                    onClick={() => handleViewModeChange('list')}
                  >
                    <ViewListIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </Grid>

            <Grid item xs={12} md="auto" sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, ml: 'auto' }}>
              <Button
                variant="outlined"
                onClick={() => navigate('/vocabulary/review')}
                disabled={loading}
              >
                Review
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddWord}
                disabled={loading || selectedLanguage === ''}
              >
                Add
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Tag Filter */}
      <Box sx={{ mb: 2 }}>
        <TagFilter
          selectedTags={selectedFilterTags}
          onTagsChange={setSelectedFilterTags}
          tagMode={tagFilterMode}
          onTagModeChange={setTagFilterMode}
        />
      </Box>

      {/* Vocabulary items */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
          <CircularProgress />
        </Box>
      ) : filteredVocabulary.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body1">
            {searchTerm ? 'No words matching your search.' : 'No vocabulary items found.'}
          </Typography>
          {searchTerm && (
            <Button
              variant="outlined"
              onClick={() => setSearchTerm('')}
              sx={{ mt: 2 }}
            >
              Clear Search
            </Button>
          )}
        </Paper>
      ) : viewMode === 'grid' ? (
        // Grid view
        <>
          <Grid container spacing={2}>
            {filteredVocabulary
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map(item => (
                <Grid item xs={12} sm={6} md={4} key={item.id}>
                  <Card variant="outlined">
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="h6" component="div" sx={{ wordBreak: 'break-word' }}>
                          {item.word}
                        </Typography>
                        <Chip
                          label={getLanguageDisplayName(item.language_code)}
                          size="small"
                          variant="outlined"
                          color="primary"
                        />
                      </Box>

                      <Typography
                        variant="body1"
                        color="text.secondary"
                        sx={{ mt: 1, wordBreak: 'break-word' }}
                      >
                        {item.translation}
                      </Typography>

                      {item.context && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            mt: 1,
                            fontStyle: 'italic',
                            opacity: 0.8,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            maxHeight: '3em',
                            lineHeight: '1.5em'
                          }}
                        >
                          "{item.context}"
                        </Typography>
                      )}

                      {/* Display tags */}
                      {item.tags && item.tags.length > 0 && (
                        <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {item.tags.map(tag => (
                            <Chip
                              key={tag.id || tag.tag_name}
                              label={tag.tag_name}
                              size="small"
                              sx={{
                                backgroundColor: tag.color ? `${tag.color}20` : undefined,
                                borderColor: tag.color || 'divider',
                                height: 20,
                                fontSize: '0.75rem'
                              }}
                              variant="outlined"
                            />
                          ))}
                        </Box>
                      )}

                      <Divider sx={{ my: 1.5 }} />

                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Knowledge Level
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                            {knowledgeLevels.map(level => (
                              <Tooltip title={level.label} key={level.value}>
                                <IconButton
                                  size="small"
                                  color={item.knowledge_level === level.value ? level.color : 'default'}
                                  onClick={() => handleUpdateKnowledgeLevel(item.id, level.value)}
                                >
                                  {React.cloneElement(level.icon, {
                                    fontSize: 'small',
                                    color: item.knowledge_level === level.value ? level.color : 'inherit'
                                  })}
                                </IconButton>
                              </Tooltip>
                            ))}
                          </Box>
                        </Box>

                        <Box>
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => handleEditWord(item)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton size="small" onClick={() => handleDeleteWord(item.id)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
          </Grid>

          <TablePagination
            component="div"
            count={filteredVocabulary.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 20, 50]}
            sx={{ mt: 2 }}
          />
        </>
      ) : (
        // List view
        <>
          <TableContainer component={Paper}>
            <Table sx={{ minWidth: 650 }} size="small">
              <TableHead>
                <TableRow>
                  <TableCell
                    onClick={() => handleSortChange('word')}
                    sx={{ cursor: 'pointer', fontWeight: 'bold' }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      Word
                      {sortField === 'word' && (
                        sortDirection === 'asc' ? <ArrowUpIcon fontSize="small" /> : <ArrowDownIcon fontSize="small" />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell
                    onClick={() => handleSortChange('translation')}
                    sx={{ cursor: 'pointer', fontWeight: 'bold' }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      Translation
                      {sortField === 'translation' && (
                        sortDirection === 'asc' ? <ArrowUpIcon fontSize="small" /> : <ArrowDownIcon fontSize="small" />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>Language</TableCell>
                  <TableCell>Tags</TableCell>
                  <TableCell
                    onClick={() => handleSortChange('knowledge_level')}
                    sx={{ cursor: 'pointer', fontWeight: 'bold' }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      Knowledge
                      {sortField === 'knowledge_level' && (
                        sortDirection === 'asc' ? <ArrowUpIcon fontSize="small" /> : <ArrowDownIcon fontSize="small" />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell
                    onClick={() => handleSortChange('last_reviewed')}
                    sx={{ cursor: 'pointer', fontWeight: 'bold' }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      Last Reviewed
                      {sortField === 'last_reviewed' && (
                        sortDirection === 'asc' ? <ArrowUpIcon fontSize="small" /> : <ArrowDownIcon fontSize="small" />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredVocabulary
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map(item => (
                    <TableRow key={item.id}>
                      <TableCell component="th" scope="row">
                        {item.word}
                      </TableCell>
                      <TableCell>{item.translation}</TableCell>
                      <TableCell>
                        <Chip
                          label={getLanguageDisplayName(item.language_code)}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {item.tags?.map(tag => (
                            <Chip
                              key={tag.id || tag.tag_name}
                              label={tag.tag_name}
                              size="small"
                              sx={{ height: 20 }}
                              variant="outlined"
                            />
                          ))}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          {knowledgeLevels.map(level => (
                            <Tooltip title={level.label} key={level.value}>
                              <IconButton
                                size="small"
                                color={item.knowledge_level === level.value ? level.color : 'default'}
                                onClick={() => handleUpdateKnowledgeLevel(item.id, level.value)}
                              >
                                {React.cloneElement(level.icon, {
                                  fontSize: 'small',
                                  color: item.knowledge_level === level.value ? level.color : 'inherit'
                                })}
                              </IconButton>
                            </Tooltip>
                          ))}
                        </Box>
                      </TableCell>
                      <TableCell>{formatDate(item.last_reviewed)}</TableCell>
                      <TableCell align="right">
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => handleEditWord(item)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" onClick={() => handleDeleteWord(item.id)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Box sx={{ width: '100%', overflowX: 'auto' }}>
            <TablePagination
              component="div"
              count={filteredVocabulary.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 20, 50]}
              sx={{
                mt: 2,
                px: 0,
                minWidth: '200px',
                '& .MuiTablePagination-toolbar': {
                  flexWrap: 'nowrap',
                  justifyContent: 'space-between'
                }
              }}
            />
          </Box>
        </>
      )}

      {/* Word Dialog with Tags */}
      <WordDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        word={dialogMode === 'edit' ? {
          id: editingItemId,
          ...newWord
        } : null}
        onSave={handleSaveWord}
        languageCode={newWord.language_code}
        playPronunciation={null}
      />

      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default VocabularyPage;